import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useHeroImage } from '../hooks/usePageHero';
import FeaturedVideo from './FeaturedVideo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Shared design tokens ──────────────────────────────────────────────────────
const SERIF  = "'Newsreader', Georgia, serif";
const SANS   = "'Public Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const NAVY   = '#16304a';
const TEAL   = '#1d6f6a';
const GOLD   = '#c79a3a';
const BEIGE  = '#f6efe2';
const TEXT   = '#2a2e31';
const MUTED  = '#5b6066';
const BORDER = '#e8e4db';
const CARD   = '#ffffff';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(ts) {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function timeAgo(ts) {
  if (!ts) return null;
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function getTag(article) {
  const raw = (article.tags && article.tags[0]) || article.primary_pillar || '';
  return raw.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Section header rule ──────────────────────────────────────────────────────
function SectionHeader({ label, linkTo, anchorId }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
      <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: '1.6px', textTransform: 'uppercase', color: TEAL, whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ width: 18, height: 2, background: GOLD, flexShrink: 0 }} />
      <span style={{ flex: '1 1 40px', height: 1, background: '#e6e2d8' }} />
      {linkTo && <Link to={linkTo} style={{ color: TEAL, fontWeight: 600, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>View All →</Link>}
    </div>
  );
}

// ─── Hero Section ─────────────────────────────────────────────────────────────
function HeroSection({ config, heroImg }) {
  const heroBg = heroImg ? `url(${heroImg}) center/cover no-repeat` : BEIGE;
  return (
    <div style={{ background: heroBg }} className="sp-hero-band" data-testid={`${config.testId}-hero`}>
      <div className="container-custom" style={{ width: '100%' }}>
        <div style={{ fontSize: 12.5, color: '#7c8893', marginBottom: 16 }}>
          <Link to="/newsroom" style={{ color: '#7c8893', textDecoration: 'none' }}>Newsroom</Link>
          <span style={{ margin: '0 8px', color: '#bcc4cb' }}>/</span>
          <Link to={config.selfPath} style={{ color: NAVY, textDecoration: 'none', fontWeight: 600 }}>{config.label}</Link>
        </div>
        <div>
          <h1 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 80, lineHeight: 0.92, margin: 0, color: NAVY, letterSpacing: '-1.5px' }} className="sp-h1" data-testid={`${config.testId}-heading`}>
            {config.h1Text}
          </h1>
          <p style={{ fontFamily: SERIF, fontSize: 21, lineHeight: 1.35, color: '#22364a', margin: '20px 0 14px', maxWidth: 680 }}>
            {config.h1Subtitle}
          </p>
          <p style={{ fontFamily: SANS, fontSize: 14, lineHeight: 1.6, color: MUTED, maxWidth: 560, margin: '0 0 26px' }}>
            {config.h1Body}
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Link to="/first-round" style={{ background: NAVY, color: '#fff', padding: '13px 20px', borderRadius: 4, fontWeight: 600, fontSize: 13.5, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 11, fontFamily: SANS }} data-testid={`${config.testId}-cta-briefing`}>
              {config.briefingCtaText}&nbsp;→
            </Link>
            <a href={`#${config.storiesAnchorId}`} style={{ background: 'transparent', border: '1px solid #b9ac8d', color: NAVY, padding: '13px 20px', borderRadius: 4, fontWeight: 600, fontSize: 13.5, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 11, fontFamily: SANS }} data-testid={`${config.testId}-cta-stories`}>
              {config.storiesCtaText}&nbsp;→
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Lead Zone ────────────────────────────────────────────────────────────────
function LeadCard({ article }) {
  const tag = getTag(article);
  const pub = formatDate(article.published_at);
  const read = article.estimated_read_minutes > 0 ? `${article.estimated_read_minutes} min read` : null;
  const meta = [pub, read].filter(Boolean).join(' · ');
  return (
    <article style={{ border: `1px solid ${BORDER}`, borderRadius: 6, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1.02fr 0.98fr', background: CARD }} data-testid="sp-lead-card">
      <div style={{ padding: '24px 22px 20px', display: 'flex', flexDirection: 'column' }}>
        <span style={{ alignSelf: 'flex-start', color: TEAL, fontWeight: 700, fontSize: 10, letterSpacing: '1.2px', textTransform: 'uppercase', background: '#eaf3f1', padding: '5px 9px', borderRadius: 3 }}>{tag}</span>
        <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 28, lineHeight: 1.12, margin: '16px 0 12px', color: NAVY }}>{article.title}</h2>
        {article.dek && <p style={{ fontSize: 14, lineHeight: 1.6, color: MUTED, margin: '0 0 16px', flex: '1 1 0' }}>{article.dek}</p>}
        <div style={{ marginTop: 'auto' }}>
          <div style={{ fontSize: 12, color: '#9a9189' }}>{meta}</div>
        </div>
        <Link to={`/insights/${article.slug}`} style={{ marginTop: 13, color: NAVY, fontWeight: 600, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 7 }}>Read Briefing →</Link>
      </div>
      <div style={{ background: '#eef1ee', minHeight: 300, overflow: 'hidden' }}>
        {article.featured_image_url
          ? <img src={article.featured_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
          : (
            <svg viewBox="0 0 290 340" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" style={{ display: 'block' }} aria-hidden="true">
              <rect width="290" height="340" fill="#eef1ee"/>
              <circle cx="150" cy="150" r="86" fill="#dbe6e2"/>
              <path d="M64 250a90 90 0 0 1 180 0z" fill="#cfddd7" opacity=".7"/>
              <circle cx="210" cy="110" r="22" fill="#c79a3a"/>
            </svg>
          )}
      </div>
    </article>
  );
}

function LeadSideCard({ article, idx }) {
  const tag = getTag(article);
  const ago = timeAgo(article.published_at);
  const read = article.estimated_read_minutes > 0 ? `${article.estimated_read_minutes} min read` : null;
  return (
    <article style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 15, display: 'grid', gridTemplateColumns: '1fr 54px', gap: 11, alignItems: 'center', background: CARD, flex: 1 }} data-testid={`sp-lead-side-${idx}`}>
      <div>
        <span style={{ display: 'block', color: TEAL, fontWeight: 700, fontSize: 9.5, letterSpacing: '1.1px', textTransform: 'uppercase', marginBottom: 7 }}>{tag}</span>
        <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 16, lineHeight: 1.2, margin: '0 0 9px', color: NAVY }}>{article.title}</h3>
        <div style={{ fontSize: 11, color: '#a39c92' }}>{[ago, read].filter(Boolean).join(' · ')}</div>
      </div>
      <div style={{ width: 54, height: 54, border: `1px solid ${BORDER}`, borderRadius: 5, overflow: 'hidden', flexShrink: 0, background: '#faf8f3' }}>
        {article.featured_image_url
          ? <img src={article.featured_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.5" aria-hidden="true"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
            </div>}
      </div>
    </article>
  );
}

function LeadZone({ articles, config }) {
  if (!articles || articles.length === 0) return null;
  const [main, ...sides] = articles;
  return (
    <section>
      <SectionHeader label="Lead Briefing" />
      <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 18, alignItems: 'stretch' }} className="sp-leadzone-grid">
        <LeadCard article={main} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
          {sides.slice(0, 3).map((a, i) => (
            <Link key={a.slug || i} to={`/insights/${a.slug}`} style={{ textDecoration: 'none', flex: 1 }}>
              <LeadSideCard article={a} idx={i + 1} />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Stories Grid (slots 5–10) ────────────────────────────────────────────────
function ArticleCard({ article, idx }) {
  const tag = getTag(article);
  const pub = formatDate(article.published_at);
  const read = article.estimated_read_minutes > 0 ? `${article.estimated_read_minutes} min read` : null;
  const meta = [pub, read].filter(Boolean).join(' · ');
  return (
    <article style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden', background: CARD, display: 'flex', flexDirection: 'column' }} data-testid={`sp-article-card-${idx}`}>
      <div style={{ height: 160, overflow: 'hidden', background: '#eef1ee', flexShrink: 0, borderBottom: `1px solid ${BORDER}` }}>
        {article.featured_image_url
          ? <img src={article.featured_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ width: 32, height: 32, borderRadius: '50%', background: TEAL, color: '#fff', fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{idx}</span>
            </div>}
      </div>
      <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', flex: '1 1 0' }}>
        <span style={{ color: TEAL, fontWeight: 700, fontSize: 10, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 10 }}>{tag}</span>
        <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 19, lineHeight: 1.2, margin: '0 0 10px', color: NAVY }}>{article.title}</h3>
        {article.dek && <p style={{ fontSize: 13, lineHeight: 1.6, color: MUTED, margin: '0 0 16px', flex: '1 1 0' }}>{article.dek}</p>}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid #ece8df', marginTop: 'auto' }}>
          <div>
            <span style={{ fontSize: 11.5, color: '#a39c92' }}>{meta}</span>
          </div>
          <Link to={`/insights/${article.slug}`} style={{ color: NAVY, fontWeight: 600, fontSize: 13, textDecoration: 'none', whiteSpace: 'nowrap', marginLeft: 8 }}>Read Briefing →</Link>
        </div>
      </div>
    </article>
  );
}

function StoriesGrid({ articles, config }) {
  if (!articles || articles.length === 0) return null;
  return (
    <section style={{ marginTop: 32 }} id={config.storiesAnchorId} data-testid={`${config.testId}-stories-grid`}>
      <SectionHeader label={config.gridLabel} linkTo={config.selfPath} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 20 }} className="sp-six-grid">
        {articles.slice(0, 6).map((a, i) => (
          <ArticleCard key={a.slug || i} article={a} idx={i + 5} />
        ))}
      </div>
    </section>
  );
}

// ─── Skeleton placeholders (loading state — exact final card dimensions) ─────
function SkeletonBox({ style }) {
  return <div className="sp-skel" style={style} />;
}

function LeadZoneSkeleton() {
  return (
    <section>
      <SectionHeader label="Lead Briefing" />
      <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 18, alignItems: 'stretch' }} className="sp-leadzone-grid">
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 6, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1.02fr 0.98fr', background: CARD, minHeight: 300 }}>
          <div style={{ padding: '24px 22px 20px', display: 'flex', flexDirection: 'column' }}>
            <SkeletonBox style={{ width: 84, height: 20, borderRadius: 3, marginBottom: 16 }} />
            <SkeletonBox style={{ width: '92%', height: 26, marginBottom: 10 }} />
            <SkeletonBox style={{ width: '68%', height: 26, marginBottom: 16 }} />
            <SkeletonBox style={{ width: '100%', height: 13, marginBottom: 7 }} />
            <SkeletonBox style={{ width: '82%', height: 13 }} />
          </div>
          <SkeletonBox style={{ minHeight: 300, borderRadius: 0 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 15, display: 'grid', gridTemplateColumns: '1fr 54px', gap: 11, alignItems: 'center', background: CARD, flex: 1, minHeight: 88 }}>
              <div>
                <SkeletonBox style={{ width: 60, height: 10, marginBottom: 9 }} />
                <SkeletonBox style={{ width: '95%', height: 15, marginBottom: 6 }} />
                <SkeletonBox style={{ width: '62%', height: 15 }} />
              </div>
              <SkeletonBox style={{ width: 54, height: 54, borderRadius: 5 }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StoriesGridSkeleton() {
  return (
    <section style={{ marginTop: 32 }}>
      <SectionHeader label="More Briefings" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 20 }} className="sp-six-grid">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden', background: CARD, display: 'flex', flexDirection: 'column' }}>
            <SkeletonBox style={{ height: 160, borderRadius: 0 }} />
            <div style={{ padding: '18px 20px 20px' }}>
              <SkeletonBox style={{ width: 72, height: 10, marginBottom: 12 }} />
              <SkeletonBox style={{ width: '90%', height: 17, marginBottom: 8 }} />
              <SkeletonBox style={{ width: '60%', height: 17, marginBottom: 16 }} />
              <SkeletonBox style={{ width: '100%', height: 12, marginBottom: 6 }} />
              <SkeletonBox style={{ width: '80%', height: 12 }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RightRailSkeleton({ count }) {
  return (
    <aside style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }} data-testid="sp-right-rail-skeleton">
      {Array.from({ length: count || 4 }).map((_, i) => (
        <div key={i} style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 16, background: CARD, minHeight: 220 }}>
          <SkeletonBox style={{ width: 100, height: 11, marginBottom: 14 }} />
          <SkeletonBox style={{ width: '100%', height: 118, borderRadius: 5, marginBottom: 14 }} />
          <SkeletonBox style={{ width: '85%', height: 16, marginBottom: 8 }} />
          <SkeletonBox style={{ width: '55%', height: 13 }} />
        </div>
      ))}
    </aside>
  );
}

// ─── Right Rail Widgets ───────────────────────────────────────────────────────
function RailLabel({ children }) {
  return (
    <div style={{ fontWeight: 700, fontSize: 10, letterSpacing: '1.3px', textTransform: 'uppercase', color: TEAL, marginBottom: 11 }}>{children}</div>
  );
}

function VideoOfWeekWidget({ config }) {
  return <FeaturedVideo scope={config.slug} />;
}

function InFocusWidget({ widget, config }) {
  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 16, background: CARD }}>
      <RailLabel>{widget.label}</RailLabel>
      <div style={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
        {widget.badgeInitials ? (
          <svg viewBox="0 0 320 150" width="100%" style={{ display: 'block' }} aria-hidden="true">
            <rect width="320" height="150" fill="#e8edf2"/>
            <rect x="100" y="20" width="120" height="110" rx="8" fill={widget.badgeBg || NAVY}/>
            <text x="160" y="88" textAnchor="middle" fill={widget.badgeColor || '#fff'} fontSize="32" fontWeight="700" fontFamily="sans-serif">{widget.badgeInitials}</text>
            <circle cx="250" cy="40" r="28" fill="#c5d4de" opacity=".4"/>
            <circle cx="50" cy="110" r="36" fill="#c5d4de" opacity=".3"/>
          </svg>
        ) : (
          <svg viewBox="0 0 320 150" width="100%" style={{ display: 'block' }} aria-hidden="true">
            <rect width="320" height="150" fill="#eef1ee"/>
            <circle cx="250" cy="60" r="40" fill="#dbe6e2"/>
            <g fill="#fff" stroke={NAVY} strokeWidth="1.2">
              <polygon points="160,46 188,66 132,66"/>
              <rect x="138" y="66" width="44" height="8"/>
              <rect x="134" y="120" width="52" height="8"/>
              <line x1="146" y1="74" x2="146" y2="120"/>
              <line x1="160" y1="74" x2="160" y2="120"/>
              <line x1="174" y1="74" x2="174" y2="120"/>
            </g>
            <g fill="#fff" stroke={NAVY} strokeWidth="1.2">
              <rect x="64" y="84" width="48" height="36"/>
              <rect x="200" y="78" width="44" height="42"/>
            </g>
            <g fill={TEAL} opacity=".7">
              <rect x="74" y="94" width="8" height="8"/>
              <rect x="92" y="94" width="8" height="8"/>
              <rect x="210" y="90" width="8" height="8"/>
              <rect x="226" y="90" width="8" height="8"/>
            </g>
          </svg>
        )}
      </div>
      <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 16, lineHeight: 1.2, margin: '13px 0 7px', color: NAVY }}>{widget.name}</h3>
      <p style={{ fontSize: 12.5, lineHeight: 1.5, color: '#7a7f84', margin: '0 0 11px' }}>{widget.description}</p>
      <Link to={config.selfPath} style={{ color: NAVY, fontWeight: 600, fontSize: 12.5, textDecoration: 'none' }}>Read Spotlight →</Link>
    </section>
  );
}

function ExecutiveSpotlightWidget({ widget, config }) {
  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 16, background: CARD }}>
      <RailLabel>Executive Spotlight</RailLabel>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 13 }}>
        <span style={{ width: 48, height: 48, borderRadius: '50%', background: NAVY, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, flexShrink: 0, fontFamily: SANS }}>{widget.initials}</span>
        <div>
          <div style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 15, color: NAVY, lineHeight: 1.2 }}>{widget.name}</div>
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>{widget.title}</div>
        </div>
      </div>
      <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#6a7075', margin: '0 0 11px', fontStyle: 'italic' }}>"{widget.description}"</p>
      <Link to={config.selfPath} style={{ color: NAVY, fontWeight: 600, fontSize: 12.5, textDecoration: 'none' }}>Read Profile →</Link>
    </section>
  );
}

function FeaturedCompanyWidget({ widget, config }) {
  const badge = widget.badge || {};
  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 16, background: CARD }}>
      <RailLabel>Featured Company</RailLabel>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 13 }}>
        <span style={{ width: 48, height: 48, borderRadius: 6, background: badge.bg || NAVY, color: badge.color || '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0, fontFamily: SANS }}>{badge.text || '?'}</span>
        <div style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 15, color: NAVY, lineHeight: 1.25 }}>{widget.name}</div>
      </div>
      <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#6a7075', margin: '0 0 11px' }}>{widget.description}</p>
      <Link to={config.selfPath} style={{ color: NAVY, fontWeight: 600, fontSize: 12.5, textDecoration: 'none' }}>View Profile →</Link>
    </section>
  );
}

function MarketPulseWidget() {
  // NOTE: Market Pulse data is NOT wired to a live data source.
  // These values are illustrative placeholders only. Wire to a real market feed in a future task.
  const items = [
    { label: 'S&P 500', value: '5,214', change: '+0.8%', up: true },
    { label: 'NASDAQ', value: '16,849', change: '+1.1%', up: true },
    { label: 'DOW', value: '38,503', change: '-0.3%', up: false },
    { label: '10-Yr', value: '4.71%', change: '+0.04', up: false },
  ];
  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 16, background: CARD }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
        <RailLabel>Market Pulse</RailLabel>
        <span style={{ fontSize: 10, color: '#b8afa4', fontStyle: 'italic', marginBottom: 11 }}>data not live</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {items.map(item => (
          <div key={item.label} style={{ padding: '8px 10px', border: `1px solid ${BORDER}`, borderRadius: 4, background: '#fafaf8' }}>
            <div style={{ fontSize: 9.5, color: '#9a9189', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: SANS }}>{item.label}</div>
            <div style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 17, color: NAVY, marginTop: 2 }}>{item.value}</div>
            <div style={{ fontSize: 11, color: item.up ? '#1d7a50' : '#c0392b', fontWeight: 600, marginTop: 2 }}>{item.change}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CtaPairWidget() {
  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: 14, background: CARD }} data-testid="sp-cta-widgets">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Link to="/first-round" style={{ display: 'flex', flexDirection: 'column', textDecoration: 'none', borderRadius: 5, overflow: 'hidden', border: `1px solid ${BORDER}`, background: '#eaf3f1' }} data-testid="sp-cta-first-round">
          <div style={{ background: NAVY, padding: '11px 13px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d9b24a" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <path d="M3 18v-6a9 9 0 0118 0v6"/><path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z"/>
            </svg>
          </div>
          <div style={{ padding: '11px 13px 13px', display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontWeight: 700, fontSize: 9, letterSpacing: '1.3px', textTransform: 'uppercase', color: TEAL }}>Daily Briefing</span>
            <span style={{ fontFamily: SERIF, fontSize: 14, lineHeight: 1.2, color: NAVY, fontWeight: 500 }}>Listen First Round</span>
            <span style={{ fontSize: 11, color: TEAL, fontWeight: 600, marginTop: 6 }}>Today's edition →</span>
          </div>
        </Link>
        <Link to="/career-hub" style={{ display: 'flex', flexDirection: 'column', textDecoration: 'none', borderRadius: 5, overflow: 'hidden', border: `1px solid ${BORDER}`, background: '#faf5ec' }} data-testid="sp-cta-career-hub">
          <div style={{ background: NAVY, padding: '11px 13px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d9b24a" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
            </svg>
          </div>
          <div style={{ padding: '11px 13px 13px', display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontWeight: 700, fontSize: 9, letterSpacing: '1.3px', textTransform: 'uppercase', color: '#9a7020' }}>Career Hub</span>
            <span style={{ fontFamily: SERIF, fontSize: 14, lineHeight: 1.2, color: NAVY, fontWeight: 500 }}>This Week's Hot Job</span>
            <span style={{ fontSize: 11, color: '#9a7020', fontWeight: 600, marginTop: 6 }}>Browse openings →</span>
          </div>
        </Link>
      </div>
    </section>
  );
}

function LeavingSoonWidget({ leavingSoon }) {
  if (!leavingSoon || leavingSoon.length === 0) return null;
  return (
    <section style={{ border: '1px solid #eed8b8', borderRadius: 6, padding: 14, background: '#fdf9f3' }} data-testid="sp-leaving-soon">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 13 }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        <span style={{ fontWeight: 700, fontSize: 10, letterSpacing: '1.3px', textTransform: 'uppercase', color: '#b45309' }}>Leaving Soon</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {leavingSoon.map((article, i) => {
          const pub = formatDate(article.published_at);
          const hrs = article.hours_left;
          const timeLeft = hrs !== undefined && hrs !== null
            ? (hrs < 24 ? `${Math.round(hrs)}h left` : `${Math.floor(hrs / 24)}d left`)
            : null;
          return (
            <Link key={article.slug || i} to={`/insights/${article.slug}`} style={{ display: 'flex', alignItems: 'stretch', border: `1px solid ${BORDER}`, borderRadius: 5, overflow: 'hidden', textDecoration: 'none', background: '#fafaf8' }} data-testid={`sp-leaving-soon-card-${i + 1}`}>
              <div style={{ width: 110, minWidth: 110, flexShrink: 0, background: '#eaf1ee', overflow: 'hidden' }}>
                {article.featured_image_url
                  ? <img src={article.featured_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 96 }} loading="lazy" />
                  : <div style={{ width: '100%', minHeight: 96, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#dfe8e5' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.5" aria-hidden="true"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
                    </div>}
              </div>
              <div style={{ padding: '11px 13px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flex: 1, minWidth: 0 }}>
                <div>
                  <span style={{ display: 'block', color: TEAL, fontWeight: 700, fontSize: 9, letterSpacing: '1.1px', textTransform: 'uppercase', marginBottom: 5 }}>Slot {article.slot}</span>
                  <h4 style={{ fontFamily: SERIF, fontSize: 14, fontWeight: 500, lineHeight: 1.25, margin: 0, color: NAVY, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{article.title}</h4>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 4 }}>
                  <span style={{ fontSize: 10.5, color: '#a39c92', flexShrink: 0 }}>{timeLeft || pub}</span>
                  <span style={{ fontSize: 11.5, color: NAVY, fontWeight: 600, whiteSpace: 'nowrap' }}>Read →</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function FirstRoundPromoWidget() {
  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 6, overflow: 'hidden', background: CARD, marginTop: 5 }} data-testid="sp-first-round-promo">
      <div style={{ background: NAVY, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(217,178,74,0.18)', border: '1px solid rgba(217,178,74,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d9b24a" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <path d="M3 18v-6a9 9 0 0118 0v6"/><path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z"/>
          </svg>
        </span>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 9.5, letterSpacing: '1.4px', textTransform: 'uppercase', color: '#d9b24a', lineHeight: 1 }}>Daily Briefing</div>
          <div style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 500, color: '#fff', lineHeight: 1.2, marginTop: 3 }}>First Round</div>
        </div>
        <Link to="/first-round" style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#d9b24a', textDecoration: 'none', whiteSpace: 'nowrap', fontFamily: SANS }} data-testid="sp-first-round-promo-cta">Listen&nbsp;→</Link>
      </div>
      <div style={{ padding: '16px 18px 18px' }}>
        <p style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 500, lineHeight: 1.3, color: NAVY, margin: '0 0 9px' }}>
          Start tomorrow with the 3-minute briefing.
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: MUTED, margin: 0 }}>
          A fast daily signal on education, careers, workforce, policy, and opportunity.
        </p>
      </div>
    </section>
  );
}

// ─── Widget dispatcher ────────────────────────────────────────────────────────
function WidgetRenderer({ widget, config, leavingSoon }) {
  switch (widget.type) {
    case 'video_of_week':       return <VideoOfWeekWidget config={config} />;
    case 'in_focus':            return <InFocusWidget widget={widget} config={config} />;
    case 'executive_spotlight': return <ExecutiveSpotlightWidget widget={widget} config={config} />;
    case 'featured_company':    return <FeaturedCompanyWidget widget={widget} config={config} />;
    case 'market_pulse':        return <MarketPulseWidget />;
    case 'cta_pair':            return <CtaPairWidget />;
    case 'leaving_soon':        return <LeavingSoonWidget leavingSoon={leavingSoon} />;
    case 'first_round_promo':   return <FirstRoundPromoWidget />;
    default:                    return null;
  }
}

// ─── Right Rail ───────────────────────────────────────────────────────────────
function RightRail({ config, leavingSoon }) {
  return (
    <aside style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }} data-testid={`${config.testId}-right-rail`}>
      {config.rightRailWidgets.map((widget, i) => (
        <WidgetRenderer key={i} widget={widget} config={config} leavingSoon={leavingSoon} />
      ))}
    </aside>
  );
}

// ─── Main SectorPage export ───────────────────────────────────────────────────
export default function SectorPage({ config, PulseBar, SponsoredBand }) {
  const heroImg = useHeroImage(config.heroSlug);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/sector/${config.slug}/articles`)
      .then(r => setArticles(r.data || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [config.slug]);

  const leadArticles  = articles.slice(0, 4);
  const gridArticles  = articles.slice(4, 10);
  const leavingSoon   = articles.slice(10, 12).filter(a => a.leaving_soon);

  return (
    <div style={{ background: CARD, color: TEXT, fontFamily: SANS, lineHeight: 1.5, WebkitFontSmoothing: 'antialiased' }} data-testid={config.testId}>
      <PulseBar />
      <HeroSection config={config} heroImg={heroImg} />

      <div className="container-custom" style={{ paddingTop: 36, paddingBottom: 48 }}>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1.9fr 1fr', columnGap: 32, alignItems: 'start' }} className="sp-main-grid">
            <div style={{ minWidth: 0 }}>
              <LeadZoneSkeleton />
              <StoriesGridSkeleton />
            </div>
            <RightRailSkeleton count={config.rightRailWidgets.length} />
          </div>
        ) : articles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: MUTED, fontFamily: SANS, fontSize: 14 }}>No briefings available in this window.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1.9fr 1fr', columnGap: 32, alignItems: 'start' }} className="sp-main-grid">
            <div style={{ minWidth: 0 }}>
              <LeadZone articles={leadArticles} config={config} />
              <StoriesGrid articles={gridArticles} config={config} />
            </div>
            <RightRail config={config} leavingSoon={leavingSoon} />
          </div>
        )}
      </div>

      <SponsoredBand />

      <style>{`
        .sp-hero-band {
          min-height: 420px;
          display: flex;
          align-items: center;
        }
        .sp-skel {
          background: linear-gradient(90deg, #eef1ee 25%, #f6f5f0 37%, #eef1ee 63%);
          background-size: 400% 100%;
          animation: sp-shimmer 1.5s ease infinite;
          border-radius: 4px;
        }
        @keyframes sp-shimmer {
          0%   { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sp-skel { animation: none; }
        }
        @media (max-width: 1080px) {
          .sp-main-grid   { grid-template-columns: 1fr !important; }
          .sp-six-grid    { grid-template-columns: repeat(2,1fr) !important; }
          .sp-hero-band   { min-height: clamp(340px, 42vw, 420px); }
        }
        @media (max-width: 680px) {
          .sp-h1             { font-size: 52px !important; }
          .sp-leadzone-grid  { grid-template-columns: 1fr !important; }
          .sp-six-grid       { grid-template-columns: 1fr !important; }
          .sp-hero-band      { min-height: clamp(300px, 62vw, 380px); }
        }
      `}</style>
    </div>
  );
}
